import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { regionSchema, IRegion } from './region';
import { IOrganization } from '../organization/organization';

/**
 * Schema for validating region form data.
 * Extends the base region schema with form-specific validations.
 *
 * @property {string} code - Region code (max 5 characters)
 * @property {string} name - Region name (max 255 characters)
 * @property {Array<OptionType>} organizations - Array of organization options with label/value pairs
 */
export const regionFormSchema = regionSchema.extend({
    organizations: z.array(optionTypeSchema).nonempty('Required'),
}).omit({
    // Omit the original ID field
    organizationIds: true,
});

/**
 * Default schema for region form initialization.
 * Extends the form schema with form-specific fields and defaults.
 * Makes nonempty string fields optional with defaults.
 *
 * @property {string} code - Optional region code
 * @property {string} name - Optional region name
 */
export const regionDefaultFormSchema = regionFormSchema.extend({
    code: z.string().optional().default(''),
    name: z.string().optional().default(''),
    organizations: z.array(optionTypeSchema).default([]),
});

/**
 * TypeScript interface for region form values.
 * Generated from the region form schema for type safety.
 */
export type IRegionFormValues = z.infer<typeof regionFormSchema>;

/**
 * TypeScript interface for region default form values.
 * Generated from the region form schema for type safety.
 */
export type IRegionDefaultFormValues = z.infer<typeof regionDefaultFormSchema>;

/**
 * Converts a Region model to form values for editing.
 * If no region is provided, returns default form values.
 *
 * @param {IRegion} [region] - Optional region model to convert
 * @param {Array<IOrganization>} [organizations] - Array of organizations to use for mapping
 * @returns {IRegionFormValues} Form values ready for editing
 */
export const RegionFormValues = (region ?: IRegion, organizations ?: Array<IOrganization>) : IRegionFormValues | IRegionDefaultFormValues => {
    // Handle the default case (new region)
    if (!region) {
        return regionDefaultFormSchema.parse({});
    }

    const { organizationIds, ...rest } = region;

    // Transform the IRegion data into IRegionFormValues structure
    return regionFormSchema.parse({
        ...rest,
        organizations: organizations?.filter(x =>
            organizationIds?.some(y => y === x.id)
        ).map(x => ({
            label: x.name,
            value: x.id,
        })) ?? [],
    });
};

/**
 * Validates region form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IRegionFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validateRegionForm = (formValues : IRegionFormValues) : boolean => {
    try {
        regionFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Region form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
